import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { downloadImportTemplate, validateImport, executeImport, getSites, exportMaterials } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  ArrowRight,
  RefreshCw,
  FileDown
} from 'lucide-react';
import { useEffect } from 'react';

const IMPORT_FIELDS = [
  'manufacturer_name',
  'manufacturer_part_number',
  'common_title',
  'material_index',
  'category',
  'material_width_mm',
  'article_number',
  'erp_article_number',
  'quantity',
  'uom',
  'number_of_rolls',
  'production_date',
  'book_value_EUR',
  'list_price_EUR',
  'is_special',
  'site'
];

export const ImportPage = () => {
  const { t } = useTranslation();
  const { user, isAdmin, isSiteManager } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1); // 1: upload, 2: validate, 3: configure, 4: result
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  
  // Data
  const [rawData, setRawData] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Import options
  const [importMode, setImportMode] = useState('enrich');
  const [selectedFields, setSelectedFields] = useState([]);
  
  // Export options
  const [exportSiteId, setExportSiteId] = useState('all');
  const [exportAgeBucket, setExportAgeBucket] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('import');

  useEffect(() => {
    if (!user || (!isAdmin && !isSiteManager)) {
      navigate('/');
      return;
    }
    loadSites();
  }, [user]);

  const loadSites = async () => {
    try {
      const response = await getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadImportTemplate();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'material_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('import.templateDownloaded') || 'Template downloaded');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error(t('import.templateError') || 'Failed to download template');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        
        setRawData(data);
        toast.success(`Loaded ${data.length} rows`);
        setStep(2);
      } catch (error) {
        console.error('Failed to parse file:', error);
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleValidate = async () => {
    setLoading(true);
    try {
      const response = await validateImport(rawData);
      setValidationResult(response.data);
      setStep(3);
    } catch (error) {
      console.error('Validation failed:', error);
      toast.error('Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    setLoading(true);
    try {
      const rowsToImport = validationResult.valid_rows;
      const response = await executeImport(
        rowsToImport, 
        importMode,
        importMode === 'selective' ? selectedFields : null
      );
      setImportResult(response.data);
      setStep(4);
      toast.success('Import completed');
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Import failed');
    } finally {
      setLoading(false);
    }
  };

  const resetImport = () => {
    setStep(1);
    setRawData([]);
    setValidationResult(null);
    setImportResult(null);
    setImportMode('enrich');
    setSelectedFields([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportSiteId !== 'all') params.site_id = exportSiteId;
      if (exportAgeBucket !== 'all') params.age_bucket = exportAgeBucket;
      
      const response = await exportMaterials(params);
      const data = response.data;
      
      if (data.length === 0) {
        toast.error(t('common.noResults'));
        return;
      }
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Materials Export');
      
      const siteSuffix = exportSiteId !== 'all' ? `_${exportSiteId}` : '';
      const ageSuffix = exportAgeBucket !== 'all' ? `_${exportAgeBucket}` : '';
      XLSX.writeFile(wb, `materials_export${siteSuffix}${ageSuffix}.xlsx`);
      toast.success(`${data.length} ${t('sustainability.materialsRedistributed').toLowerCase()} exportiert`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!user || (!isAdmin && !isSiteManager)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="import-page">
      <Header />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-manrope text-3xl font-bold">{t('import.title')}</h1>
          <p className="text-muted-foreground">{t('import.subtitle') || 'Import & Export materials via Excel'}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="import" className="gap-2" data-testid="import-tab">
              <Upload className="h-4 w-4" />
              {t('nav.import')}
            </TabsTrigger>
            <TabsTrigger value="export" className="gap-2" data-testid="export-tab">
              <FileDown className="h-4 w-4" />
              {t('import.export') || 'Export'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="import" className="space-y-6">

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 max-w-xl">
          {['Upload', 'Validate', 'Configure', 'Result'].map((label, i) => (
            <div key={label} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step > i + 1 ? 'bg-green-500 text-white' :
                step === i + 1 ? 'bg-orange-500 text-white' :
                'bg-slate-200 dark:bg-slate-700 text-slate-500'
              }`}>
                {step > i + 1 ? <CheckCircle className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`ml-2 text-sm ${step >= i + 1 ? 'font-medium' : 'text-muted-foreground'}`}>
                {label}
              </span>
              {i < 3 && <ArrowRight className="h-4 w-4 mx-4 text-slate-300" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Upload Excel File</CardTitle>
              <CardDescription>
                Download the template first, fill in your data, then upload
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                {t('import.downloadTemplate')}
              </Button>

              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                <p className="text-muted-foreground mb-4">
                  Drag & drop your Excel file here, or click to browse
                </p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="max-w-xs mx-auto"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Validate */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Review Data</CardTitle>
              <CardDescription>
                {rawData.length} rows loaded from file
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ScrollArea className="h-64 border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {IMPORT_FIELDS.slice(0, 6).map((field) => (
                        <TableHead key={field}>{field}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rawData.slice(0, 10).map((row, i) => (
                      <TableRow key={i}>
                        {IMPORT_FIELDS.slice(0, 6).map((field) => (
                          <TableCell key={field}>{row[field] || '-'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              
              {rawData.length > 10 && (
                <p className="text-sm text-muted-foreground text-center">
                  Showing 10 of {rawData.length} rows
                </p>
              )}

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={handleValidate} 
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('import.validate')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Configure */}
        {step === 3 && validationResult && (
          <div className="space-y-6">
            {/* Validation Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Validation Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{validationResult.valid_count}</p>
                    <p className="text-sm text-muted-foreground">Valid Rows</p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{validationResult.error_count}</p>
                    <p className="text-sm text-muted-foreground">Errors</p>
                  </div>
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-600">
                      {validationResult.valid_rows?.filter(r => r.is_duplicate).length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Duplicates</p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {validationResult.valid_rows?.filter(r => !r.is_duplicate).length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">New Items</p>
                  </div>
                </div>

                {validationResult.errors?.length > 0 && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Validation Errors</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-4 mt-2">
                        {validationResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>Row {err.row}: {err.errors.join(', ')}</li>
                        ))}
                      </ul>
                      {validationResult.errors.length > 5 && (
                        <p className="mt-2">...and {validationResult.errors.length - 5} more errors</p>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Import Mode Selection */}
            {validationResult.valid_count > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Import Mode</CardTitle>
                  <CardDescription>
                    Choose how to handle duplicate records
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <RadioGroup value={importMode} onValueChange={setImportMode}>
                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                      <RadioGroupItem value="enrich" id="enrich" />
                      <div>
                        <Label htmlFor="enrich" className="font-medium cursor-pointer">
                          {t('import.mode.enrich')}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Only fill empty fields, keep existing data
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                      <RadioGroupItem value="overwrite" id="overwrite" />
                      <div>
                        <Label htmlFor="overwrite" className="font-medium cursor-pointer">
                          {t('import.mode.overwrite')}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Replace all fields with imported values
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                      <RadioGroupItem value="selective" id="selective" />
                      <div>
                        <Label htmlFor="selective" className="font-medium cursor-pointer">
                          {t('import.mode.selective')}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Choose specific fields to update
                        </p>
                      </div>
                    </div>
                  </RadioGroup>

                  {importMode === 'selective' && (
                    <div className="pl-4 space-y-2">
                      <Label>Select fields to update:</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {IMPORT_FIELDS.filter(f => !['site_id', 'article_number'].includes(f)).map((field) => (
                          <div key={field} className="flex items-center space-x-2">
                            <Checkbox
                              id={field}
                              checked={selectedFields.includes(field)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedFields(prev => [...prev, field]);
                                } else {
                                  setSelectedFields(prev => prev.filter(f => f !== field));
                                }
                              }}
                            />
                            <Label htmlFor={field} className="text-sm cursor-pointer">
                              {field}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(2)}>
                      Back
                    </Button>
                    <Button 
                      onClick={handleExecuteImport} 
                      disabled={loading || (importMode === 'selective' && selectedFields.length === 0)}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {t('import.execute')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && importResult && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{importResult.created}</p>
                  <p className="text-sm text-muted-foreground">Created</p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResult.updated}</p>
                  <p className="text-sm text-muted-foreground">Updated</p>
                </div>
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-yellow-600">{importResult.enriched}</p>
                  <p className="text-sm text-muted-foreground">Enriched</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{importResult.overwritten}</p>
                  <p className="text-sm text-muted-foreground">Overwritten</p>
                </div>
              </div>

              {importResult.rejected_count > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>{importResult.rejected_count} rows rejected</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-4 mt-2">
                      {importResult.rejected?.slice(0, 5).map((rej, i) => (
                        <li key={i}>{rej.error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-4">
                <Button variant="outline" onClick={resetImport}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Import More
                </Button>
                <Button 
                  className="bg-orange-500 hover:bg-orange-600"
                  onClick={() => navigate('/')}
                >
                  View Materials
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-6">
            <Card data-testid="export-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileDown className="h-5 w-5 text-emerald-500" />
                  {t('import.exportTitle') || 'Export Materials'}
                </CardTitle>
                <CardDescription>
                  {t('import.exportDesc') || 'Download material data as an Excel file with optional filters'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('filter.site')}</Label>
                    <Select value={exportSiteId} onValueChange={setExportSiteId}>
                      <SelectTrigger data-testid="export-site-filter">
                        <SelectValue placeholder={t('dashboard.allSites')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('dashboard.allSites')}</SelectItem>
                        {sites.map(site => (
                          <SelectItem key={site.site_id} value={site.site_id}>
                            {site.site_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('filter.age')}</Label>
                    <Select value={exportAgeBucket} onValueChange={setExportAgeBucket}>
                      <SelectTrigger data-testid="export-age-filter">
                        <SelectValue placeholder={t('dashboard.allSites')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('dashboard.allSites')}</SelectItem>
                        <SelectItem value="0_90">{t('dashboard.age0_90')}</SelectItem>
                        <SelectItem value="91_180">{t('dashboard.age91_180')}</SelectItem>
                        <SelectItem value="181_360">{t('dashboard.age181_360')}</SelectItem>
                        <SelectItem value="gt_360">{t('dashboard.ageGt360')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleExport} 
                  disabled={exporting}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="export-download-btn"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {t('import.exportDownload') || 'Download Excel'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
